import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ImageUploading from "react-images-uploading";
import { toast } from "react-toastify";
import { uploadImage } from "../../../redux/reducers/postApiSlice";
import { commonPostPutFormData } from "../../../redux/reducers/commonApiSlice";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import {
  categoryTypes,
  validateAddonCategoryForm,
} from "./AddonCategoriesData";
import { FireIcon, FolderIcon, XIcon } from "../../../images/icons/icons";
import DotLoaders from "../../common-components/loaders/DotLoaders";

const AddonCategoryForm = ({
  isEdit,
  rowData,
  closeModal,
  userInfoData,
  callFunction,
}) => {
  const dispatch = useDispatch();
  const { postPutLoadingFormData } = useSelector((state) => state.common);

  const getCategoryTypeFromFlags = (data) => {
    if (data?.is_recommended) return "recommended";
    if (data?.is_live) return "live";
    if (data?.is_pt) return "pt";
    if (data?.is_free) return "book_free_trial";
    return "";
  };

  const initialFormValues = {
    name: isEdit && rowData?.name ? rowData.name : "",
    description: isEdit && rowData?.description ? rowData.description : "",
    categoryType: isEdit ? getCategoryTypeFromFlags(rowData) : "",
    isActive: isEdit && rowData?.status === "active" ? true : false,
    categoryImage: isEdit && rowData?.image ? rowData.image : "",
  };
  const [formValues, setFormValues] = useState(initialFormValues);
  const [formImages, setFormImages] = useState({
    categoryImage: rowData?.image ? [{ data_url: rowData.image }] : [],
  });
  const [formErrors, setFormErrors] = useState({});

  const onChangeInput = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onChangeImages = (imageList, name) => {
    setFormImages((prevImages) => ({
      ...prevImages,
      [name]: imageList,
    }));

    if (imageList.length > 0) {
      const latestImage = imageList[imageList.length - 1];

      (async () => {
        const blob = await fetch(latestImage?.data_url).then((res) =>
          res.blob()
        );
        const file = new File([blob], "filename.jpeg", { type: "image/jpeg" });
        handleUploadImage(file, name);
      })();
    }
  };

  const handleImageRemove = (index) => {
    setFormImages((prevImages) => {
      const updatedImages = [...prevImages.categoryImage];
      updatedImages.splice(index, 1);
      return {
        ...prevImages,
        categoryImage: updatedImages,
      };
    });

    setFormValues((prevValues) => ({
      ...prevValues,
      categoryImage: "",
    }));
  };

  const handleUploadImage = async (file, name) => {
    const TOKEN = userInfoData?.token;

    try {
      const actionResult = dispatch(uploadImage({ file, TOKEN }));
      const result = await actionResult;

      if (result?.payload?.status) {
        setFormValues((prev) => ({
          ...prev,
          categoryImage: result?.payload?.image_url || "",
        }));

        toast.success(
          result?.payload?.message || "Image uploaded successfully!"
        );
      } else {
        const errorMessage = result?.error?.message || result?.payload?.message;
        toast.error(errorMessage || "Image upload failed!");
      }
    } catch (error) {
      console.log("handleUploadImage ERROR => ", error);
      toast.error("Image upload failed!");
    }
  };

  const handleSubmitForm = async (userData) => {
    const method = isEdit ? "PUT" : "POST";
    const endpoint = isEdit ? "/addoncat/update" : "/addoncat/add";
    const token = userInfoData?.token || "";

    const formData = new FormData();
    for (const key in userData) {
      if (userData.hasOwnProperty(key)) {
        if (userData[key] instanceof File) {
          formData.append(key, userData[key]);
        } else {
          formData.append(key, userData[key].toString());
        }
      }
    }

    const requestBody = formData;

    try {
      const result = await dispatch(
        commonPostPutFormData({ method, endpoint, requestBody, token })
      );

      if (result?.payload?.status) {
        toast.success(
          result?.payload?.message || "Category saved successfully!"
        );
        callFunction();
        closeModal();
      } else {
        const errorMessage =
          result?.error?.message ||
          result?.payload?.message ||
          "Failed to save category!";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error making POST request:", error);
      toast.error("Something went wrong!");
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errors = validateAddonCategoryForm(formValues);
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      const userData = {
        category_name: formValues.name || "",
        description: formValues.description || "",
        status: formValues.isActive ? "active" : "inactive",
        image: formValues.categoryImage || "",
        is_recommended: formValues.categoryType === "recommended" ? 1 : 0,
        is_live: formValues.categoryType === "live" ? 1 : 0,
        is_pt: formValues.categoryType === "pt" ? 1 : 0,
        is_free: formValues.categoryType === "book_free_trial" ? 1 : 0,
      };

      if (isEdit) {
        userData.uid = rowData?.uid || "";
      }

      const handleConfirm = async () => {
        handleSubmitForm(userData);
      };

      showConfirmAlert(
        handleConfirm,
        `Are you sure!`,
        "Edit Form",
        "Submit Form"
      );
    }
  };

  return (
    <div className="addon-cat-form-modal">
      <div className="addon-cat-header">
        <div className="addon-cat-header-left">
          <div className="addon-cat-header-icon">
            <FolderIcon />
          </div>
          <div className="addon-cat-header-text">
            <h2>{isEdit ? "Edit" : "Add"} Addon Category</h2>
            <p>
              Create something epic <FireIcon color="#FF0000" />
            </p>
          </div>
        </div>
        <button
          type="button"
          className="addon-cat-header-close"
          onClick={closeModal}
          aria-label="Close"
        >
          <XIcon />
        </button>
      </div>

      <form className="addon-cat-form" onSubmit={onSubmit}>
        <div className="addon-cat-form-group">
          <label htmlFor="name" className="addon-cat-label">
            Name <span className="addon-cat-required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            placeholder="Enter addon name..."
            className={`addon-cat-input ${
              formErrors.name ? "addon-cat-error" : ""
            }`}
            value={formValues.name}
            onChange={onChangeInput}
          />
          {formErrors.name && (
            <span className="addon-cat-error-msg">{formErrors.name}</span>
          )}
        </div>

        <div className="addon-cat-form-group">
          <label htmlFor="description" className="addon-cat-label">
            Description <span className="addon-cat-required">*</span>
          </label>
          <textarea
            id="description"
            name="description"
            placeholder="Describe your addon..."
            className={`addon-cat-textarea ${
              formErrors.description ? "addon-cat-error" : ""
            }`}
            value={formValues.description}
            onChange={onChangeInput}
            rows={4}
          />
          {formErrors.description && (
            <span className="addon-cat-error-msg">
              {formErrors.description}
            </span>
          )}
        </div>

        <div className="addon-cat-form-group">
          <label className="addon-cat-label">
            Image <span className="addon-cat-dimension">(1920 x 1080px)</span>
          </label>
          <div
            className={`addon-cat-upload-area ${
              formErrors.categoryImage ? "addon-cat-error" : ""
            }`}
          >
            <ImageUploading
              multiple={false}
              value={formImages.categoryImage}
              onChange={(imageList, _) =>
                onChangeImages(imageList, "categoryImage")
              }
              maxNumber={1}
              dataURLKey="data_url"
              acceptType={["jpg", "jpeg", "png", "webp"]}
            >
              {({
                imageList,
                onImageUpload,
                onImageUpdate,
                onImageRemove,
                isDragging,
                dragProps,
              }) => (
                <div className="addon-cat-upload-wrapper">
                  {formImages.categoryImage?.length === 0 ? (
                    <div
                      className="addon-cat-upload-empty"
                      onClick={onImageUpload}
                      {...dragProps}
                    >
                      <div className="addon-cat-upload-icon">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M17 8L12 3L7 8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 3V15"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="addon-cat-upload-text">
                        <p className="addon-cat-upload-title">Choose File</p>
                        <p className="addon-cat-upload-subtitle">
                          or drag and drop here
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {imageList.map((image, index) => (
                        <div key={index} className="addon-cat-image-preview">
                          <img src={image["data_url"]} alt="Category" />
                          <div className="addon-cat-image-actions">
                            <button
                              type="button"
                              className="addon-cat-image-btn addon-cat-image-btn-update"
                              onClick={() => onImageUpdate(index)}
                              title="Update Image"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M8 5.5a.5.5 0 0 1 .5.5v1.5H10a.5.5 0 0 1 0 1H8.5V10a.5.5 0 0 1-1 0V8.5H6a.5.5 0 0 1 0-1h1.5V6a.5.5 0 0 1 .5-.5z"
                                />
                                <path d="M4.406 3.342A5.53 5.53 0 0 1 8 2c2.69 0 4.923 2 5.166 4.579C14.758 6.804 16 8.137 16 9.773 16 11.569 14.502 13 12.687 13H3.781C1.708 13 0 11.366 0 9.318c0-1.763 1.266-3.223 2.942-3.593.143-.863.698-1.723 1.464-2.383zm.653.757c-.757.653-1.153 1.44-1.153 2.056v.448l-.445.049C2.064 6.805 1 7.952 1 9.318 1 10.785 2.23 12 3.781 12h8.906C13.98 12 15 10.988 15 9.773c0-1.216-1.02-2.228-2.313-2.228h-.5v-.5C12.188 4.825 10.328 3 8 3a4.53 4.53 0 0 0-2.941 1.1z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="addon-cat-image-btn addon-cat-image-btn-remove"
                              onClick={() => {
                                onImageRemove(index);
                                handleImageRemove(index);
                              }}
                              title="Remove Image"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="currentColor"
                              >
                                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </ImageUploading>
          </div>
          {formErrors.categoryImage && (
            <span className="addon-cat-error-msg">
              {formErrors.categoryImage}
            </span>
          )}
        </div>

        <div className="addon-cat-status-toggle">
          <label className="addon-cat-toggle">
            <input
              type="checkbox"
              name="isActive"
              checked={formValues.isActive}
              onChange={onChangeInput}
            />
            <div className="addon-cat-toggle-label-wrapper">
              <span className="addon-cat-toggle-status-dot"></span>
              <span className="addon-cat-toggle-label">
                {formValues.isActive ? "Active" : "Inactive"}
              </span>
            </div>
            <span className="addon-cat-toggle-slider"></span>
          </label>
        </div>

        <div className="addon-cat-form-group">
          <label className="addon-cat-label">Category Type</label>
          <div className="addon-cat-type-grid">
            {categoryTypes.map((type) => (
              <label
                key={type.value}
                className={`addon-cat-type-card ${
                  formValues.categoryType === type.value
                    ? "addon-cat-type-card-active"
                    : ""
                }`}
              >
                <input
                  type="radio"
                  name="categoryType"
                  value={type.value}
                  checked={formValues.categoryType === type.value}
                  onChange={onChangeInput}
                />
                <div className="addon-cat-type-icon">{type.icon}</div>
                <div className="addon-cat-type-label">{type.label}</div>
              </label>
            ))}
          </div>
        </div>

        <div className="addon-cat-form-actions">
          <button
            type="button"
            className="addon-cat-btn addon-cat-btn-secondary"
            onClick={closeModal}
            disabled={postPutLoadingFormData}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="addon-cat-btn addon-cat-btn-primary"
            disabled={postPutLoadingFormData}
          >
            {postPutLoadingFormData ? <DotLoaders /> : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddonCategoryForm;
